import React, { useMemo, useRef } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { useSettings } from '../../hooks/useSettings'
import {
  CONTACT_EMAIL,
  formatPrice,
} from '../RechargingStation/RechargingStationItems/Subscription'
import ModuleCard from './ModuleCard'
import { costsTone } from './costsTone'
import {
  formatNumber,
  hasPlanCredits,
  listCurrency,
  planLabel,
  pricePerCredit,
  rateEntries,
} from './costsFormat'
import useCostsData, { useNearViewport } from './useCostsData'

const Email = ({ tone }) => (
  <a href={`mailto:${CONTACT_EMAIL}`} className={clsx('font-medium whitespace-nowrap', tone.link)}>
    {CONTACT_EMAIL}
  </a>
)

const SectionHeading = ({ id, eyebrow, title, lead, tone }) => (
  <header className="mb-8 max-w-3xl">
    <p className={clsx('mb-3 text-xs font-semibold uppercase tracking-[0.22em]', tone.accent)}>
      {eyebrow}
    </p>
    <h3 id={id} className={clsx('text-2xl laptop:text-3xl font-bold tracking-tight', tone.text)}>
      {title}
    </h3>
    {lead && (
      <p className={clsx('mt-3 text-base laptop:text-lg font-light leading-relaxed', tone.muted)}>
        {lead}
      </p>
    )}
  </header>
)

const Skeleton = ({ tone, count = 3, className }) => (
  <ul aria-hidden="true" className={className}>
    {Array.from({ length: count }, (_, i) => (
      <li
        key={i}
        className={clsx('h-48 rounded-2xl motion-safe:animate-pulse', tone.skeleton)}
      />
    ))}
  </ul>
)

const LoadError = ({ tone, testId }) => {
  const { t } = useTranslation('costs')
  return (
    <p data-testid={testId} role="status" className={clsx('text-sm', tone.muted)}>
      {t('loadError')} <Email tone={tone} />
    </p>
  )
}

// One quota under the other, label above its value, across the whole card: a long label
// ("Lavori in parallelo", "Archiviazione") has the card's width instead of half of it, and
// never runs into the other quota or out of the card.
const Stat = ({ label, value, tone }) => (
  <div className="min-w-0">
    <dt className={clsx('break-words text-[11px] uppercase tracking-[0.16em]', tone.faint)}>
      {label}
    </dt>
    <dd className={clsx('mt-1 text-lg font-semibold', tone.text)}>{value}</dd>
  </div>
)

export const PLANS_GRID = 'relative grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] gap-5'

/** One step of the ladder demo -> standard -> pro -> enterprise. */
const PlanCard = ({ plan, isDemo, step, steps, sectionId, tone, lang, choosePlanHref }) => {
  const { t } = useTranslation('costs')
  const name = t(`payments:subscription.tiers.${plan.id}`, { defaultValue: plan.id })
  const quotas = plan.quotas || {}
  const dash = '—'
  const credits = typeof plan.planCredits === 'number' ? formatNumber(plan.planCredits, lang) : null

  return (
    // Four rows of the ladder's grid (subgrid): header, quotas, plan credits, link. Cards of
    // the same row share each row's height, so a price on two lines ("Nessun pagamento per
    // iniziare") does not push that card's quotas below the others.
    <li
      data-testid={`costs-plan-${plan.id}`}
      className={clsx(
        'relative row-span-4 grid grid-rows-[subgrid] gap-y-5 rounded-2xl p-6',
        tone.card,
        tone.cardHover,
        isDemo && tone.highlight
      )}
    >
      {/* The ladder, drawn: the bar grows one step per plan. */}
      <div aria-hidden="true" className={clsx('absolute left-6 right-6 top-0 h-[2px] rounded-full', tone.track)}>
        <div
          className={clsx('h-[2px] rounded-full', tone.rail)}
          style={{ width: `${Math.round((step / Math.max(steps, 1)) * 100)}%` }}
        />
      </div>
      <div>
        <p className={clsx('text-xs font-semibold uppercase tracking-[0.18em]', tone.accent)}>
          {isDemo ? t('plans.demoEyebrow') : t('plans.planEyebrow', { step })}
        </p>
        <h4 className={clsx('mt-2 text-2xl font-bold', tone.text)}>{name}</h4>
        <p
          data-testid={`costs-plan-${plan.id}-price`}
          className={clsx('mt-2 text-base font-medium', tone.text)}
        >
          {isDemo ? t('plans.demoPrice') : t(...planLabel(plan.price, lang))}
        </p>
        {isDemo && typeof plan.days === 'number' && (
          <p data-testid="costs-plan-demo-days" className={clsx('mt-1 text-sm', tone.muted)}>
            {t('plans.demoDays', { count: plan.days })}
          </p>
        )}
      </div>
      <dl className={clsx('flex flex-col gap-4 border-t pt-5', tone.divider)}>
        <Stat
          tone={tone}
          label={t('plans.jobs')}
          value={quotas.maxConcurrentJobs ?? dash}
        />
        <Stat
          tone={tone}
          label={t('plans.storage')}
          value={
            typeof quotas.maxStorageGb === 'number'
              ? t('plans.storageValue', { value: formatNumber(quotas.maxStorageGb, lang) })
              : dash
          }
        />
      </dl>
      {/* The two last rows always exist, empty when there is nothing to say. */}
      <div>
        {credits !== null && (
          <p data-testid={`costs-plan-${plan.id}-credits`} className={clsx('text-sm', tone.muted)}>
            {isDemo
              ? t('plans.demoCredits', { credits })
              : t('plans.planCredits', { credits })}
          </p>
        )}
      </div>
      <div className="self-end">
        {isDemo && (
          <a href={`#${sectionId}-demo`} className={clsx('text-sm', tone.link)}>
            {t('plans.demoMore')}
          </a>
        )}
        {/* With a session: plans are bought, changed and cancelled in the Subscription tab of
            the recharging station (spec digiscan-web 2026-10-26 §8), which knows who may. */}
        {!isDemo && choosePlanHref && plan.price && (
          <a
            href={choosePlanHref}
            data-testid={`costs-plan-${plan.id}-choose`}
            className={clsx('text-sm', tone.link)}
          >
            {t('plans.choose')}
          </a>
        )}
      </div>
    </li>
  )
}

/**
 * Plans, modules, credits and the demo, from the server (ticket 6ad5d51d).
 *
 * Nothing priced is written here: plans and modules come from /payments/price-list,
 * credit packs from /payments/packages (the recharging station's own source), the rate
 * card from /billing/rates. Each one loads on its own, when the section nears the
 * viewport, and a failure says so instead of showing a zero.
 *
 * `variant`: 'landing' (login page, dark blue) or 'themed' (recharging station).
 * `moduleImages` (optional): { [moduleId]: { src, alt } } for the module cards. None yet,
 * ticket 6ad9db16e13e5eeed86b140b.
 * `choosePlanHref` (optional, only with a session): where a priced plan card sends the user
 * to subscribe, i.e. the Subscription tab of the recharging station.
 */
const CostsOverview = ({
  id = 'costs',
  variant = 'landing',
  showIntro = true,
  moduleImages = {},
  choosePlanHref = null,
}) => {
  const { t, i18n } = useTranslation('costs')
  const { isDarkMode } = useSettings()
  const tone = costsTone(variant, isDarkMode)
  const lang = i18n?.language || 'en'

  const ref = useRef(null)
  const near = useNearViewport(ref)
  const { priceList, packages, rates } = useCostsData(near)

  const list = priceList.status === 'ready' ? priceList.data : null
  const plans = Array.isArray(list?.plans) ? list.plans : []
  const modules = Array.isArray(list?.modules) ? list.modules : []
  const demo = list?.demo && typeof list.demo === 'object' ? list.demo : null
  const packs =
    packages.status === 'ready' && Array.isArray(packages.data) ? packages.data : []
  const rateRows = rates.status === 'ready' ? rateEntries(rates.data) : []
  const currency = listCurrency(list, packs)
  const listLoading = priceList.status === 'idle' || priceList.status === 'loading'

  // Reverse edges of the dependency graph: who needs this module.
  const neededBy = useMemo(() => {
    const map = {}
    modules.forEach((m) =>
      (Array.isArray(m.requires) ? m.requires : []).forEach((req) =>
        (Array.isArray(req?.anyOf) ? req.anyOf : []).forEach((target) => {
          if (!(map[target] || []).includes(m.id)) map[target] = [...(map[target] || []), m.id]
        })
      )
    )
    return map
  }, [modules])

  const ladder = [...(demo ? [{ ...demo, id: 'demo' }] : []), ...plans]
  const cardsGrid = 'grid grid-cols-1 gap-5 tablet:grid-cols-2 laptop:grid-cols-3'

  return (
    <section
      id={id}
      ref={ref}
      aria-labelledby={`${id}-title`}
      data-testid="costs-overview"
      // focusable from script only: the arrow on the login page moves the focus here
      tabIndex={-1}
      // Inside a host (the recharging station) the section takes the host's background.
      className={clsx(
        // No scroll margin: the arrow brings the section to the very top, without the
        // strip of login picture a margin would leave above it.
        'relative focus:outline-none',
        variant === 'landing' ? tone.section : 'bg-transparent'
      )}
    >
      {variant === 'landing' && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 overflow-hidden"
          style={{
            background:
              'radial-gradient(60% 40% at 15% 0%, rgba(100,140,181,0.22) 0%, rgba(15,26,51,0) 70%), radial-gradient(50% 35% at 90% 30%, rgba(140,183,237,0.12) 0%, rgba(15,26,51,0) 70%)',
          }}
        />
      )}
      <div
        className={clsx(
          'relative mx-auto flex w-full max-w-[1200px] flex-col gap-20',
          variant === 'landing' ? 'px-5 py-20 tablet:px-10 laptop:py-28' : 'py-2'
        )}
      >
        {showIntro && (
          <header className="max-w-3xl">
            <p className={clsx('mb-4 text-xs font-semibold uppercase tracking-[0.3em]', tone.accent)}>
              {t('intro.eyebrow')}
            </p>
            <h2
              id={`${id}-title`}
              className={clsx(
                'text-4xl laptop:text-6xl font-bold leading-[1.05] tracking-tight',
                tone.text
              )}
            >
              {t('intro.title')}
            </h2>
            <p className={clsx('mt-6 text-lg laptop:text-xl font-light leading-relaxed', tone.muted)}>
              {t('intro.lead')}
            </p>
          </header>
        )}
        {!showIntro && (
          <h2 id={`${id}-title`} className="sr-only">
            {t('intro.title')}
          </h2>
        )}

        {/* Modules */}
        <div>
          <SectionHeading
            id={`${id}-modules`}
            tone={tone}
            eyebrow={t('modules.eyebrow')}
            title={t('modules.title')}
            lead={t('modules.lead')}
          />
          {listLoading && <Skeleton tone={tone} count={6} className={cardsGrid} />}
          {priceList.status === 'error' && <LoadError tone={tone} testId="costs-modules-error" />}
          {list && (
            <ul className={cardsGrid} data-testid="costs-modules">
              {modules.map((m) => (
                <ModuleCard
                  key={m.id}
                  module={m}
                  sectionId={id}
                  tone={tone}
                  lang={lang}
                  neededBy={neededBy[m.id] || []}
                  image={moduleImages[m.id] || null}
                />
              ))}
            </ul>
          )}
        </div>

        {/* Plans */}
        <div>
          <SectionHeading
            id={`${id}-plans`}
            tone={tone}
            eyebrow={t('plans.eyebrow')}
            title={t('plans.title')}
            lead={[t('plans.lead'), hasPlanCredits(list) ? t('plans.leadCredits') : '']
              .filter(Boolean)
              .join(' ')}
          />
          {listLoading && (
            <Skeleton tone={tone} count={4} className={PLANS_GRID} />
          )}
          {priceList.status === 'error' && <LoadError tone={tone} testId="costs-plans-error" />}
          {list && (
            <div className="relative">
              <ol
                data-testid="costs-plans"
                // Columns from the width of the container, not of the screen: inside the
                // recharging station (~870 px) the four cards keep room for their labels.
                className={PLANS_GRID}
              >
                {ladder.map((plan, index) => (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    step={index + 1}
                    steps={ladder.length}
                    isDemo={plan.id === 'demo'}
                    sectionId={id}
                    tone={tone}
                    lang={lang}
                    choosePlanHref={choosePlanHref}
                  />
                ))}
              </ol>
            </div>
          )}
          <p className={clsx('mt-6 text-sm', tone.muted)}>
            {t('payments:subscription.dedicatedNote')} <Email tone={tone} />
          </p>
        </div>

        {/* Credits */}
        <div>
          <SectionHeading
            id={`${id}-credits`}
            tone={tone}
            eyebrow={t('credits.eyebrow')}
            title={t('credits.title')}
            lead={t('credits.lead')}
          />
          <div className="grid grid-cols-1 gap-8 laptop:grid-cols-[1.2fr_1fr]">
            <div>
              <h4 className={clsx('mb-4 text-sm font-semibold uppercase tracking-[0.16em]', tone.faint)}>
                {t('credits.packsTitle')}
              </h4>
              {(packages.status === 'idle' || packages.status === 'loading') && (
                <Skeleton tone={tone} count={3} className="grid grid-cols-1 gap-4 tablet:grid-cols-3" />
              )}
              {packages.status === 'error' && <LoadError tone={tone} testId="costs-packs-error" />}
              {packages.status === 'ready' && (
                <ul data-testid="costs-packs" className="grid grid-cols-1 gap-4 tablet:grid-cols-3">
                  {packs.map((pkg) => {
                    const each = formatPrice(pricePerCredit(pkg), lang)
                    return (
                      <li
                        key={`${pkg.credits}-${pkg.amountCents}`}
                        data-testid={`costs-pack-${pkg.credits}`}
                        className={clsx('flex flex-col gap-1 rounded-2xl p-5', tone.card, tone.cardHover)}
                      >
                        <p className={clsx('text-3xl font-bold tracking-tight', tone.text)}>
                          {formatNumber(pkg.credits, lang)}
                        </p>
                        <p className={clsx('text-xs uppercase tracking-[0.16em]', tone.faint)}>
                          {t('credits.unit')}
                        </p>
                        <p className={clsx('mt-3 text-base font-medium', tone.text)}>
                          {formatPrice(pkg, lang) || t('payments:subscription.contactForPrice')}
                        </p>
                        {each && (
                          <p className={clsx('text-xs', tone.muted)}>
                            {t('credits.perCredit', { price: each })}
                          </p>
                        )}
                      </li>
                    )
                  })}
                </ul>
              )}
              <ul className={clsx('mt-6 flex flex-col gap-3 text-sm leading-relaxed', tone.muted)}>
                <li className="flex gap-3">
                  <span aria-hidden="true" className={tone.accent}>●</span>
                  {t('credits.neverExpire')}
                </li>
                {hasPlanCredits(list) && (
                  <li className="flex gap-3" data-testid="costs-plan-credits-rule">
                    <span aria-hidden="true" className={tone.accent}>●</span>
                    {t('credits.planFirst')}
                  </li>
                )}
                <li className="flex gap-3">
                  <span aria-hidden="true" className={tone.accent}>●</span>
                  {t('credits.howToBuy')}
                </li>
              </ul>
            </div>
            <div className={clsx('rounded-2xl p-6', tone.card)}>
              <h4 className={clsx('text-sm font-semibold uppercase tracking-[0.16em]', tone.faint)}>
                {t('credits.ratesTitle')}
              </h4>
              {rates.status === 'ready' && rateRows.length > 0 ? (
                <dl data-testid="costs-rates" className="mt-4 flex flex-col">
                  {rateRows.map((row) => (
                    <div
                      key={row.id}
                      data-testid={`costs-rate-${row.id}`}
                      className={clsx('flex items-baseline justify-between gap-4 border-b py-4 last:border-b-0', tone.divider)}
                    >
                      <dt className={clsx('text-sm', tone.muted)}>{t(`credits.rates.${row.id}`)}</dt>
                      <dd className={clsx('text-right text-lg font-semibold', tone.text)}>
                        {t('credits.rateValue', { value: formatNumber(row.value, lang) })}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : rates.status === 'idle' || rates.status === 'loading' ? (
                <div aria-hidden="true" className={clsx('mt-4 h-24 rounded-xl motion-safe:animate-pulse', tone.skeleton)} />
              ) : (
                <p data-testid="costs-rates-unavailable" className={clsx('mt-4 text-sm', tone.muted)}>
                  {t('credits.ratesUnavailable')}
                </p>
              )}
              <p className={clsx('mt-4 text-sm leading-relaxed', tone.muted)}>{t('credits.measured')}</p>
              <p className={clsx('mt-3 text-sm leading-relaxed', tone.muted)}>{t('credits.storage')}</p>
            </div>
          </div>
        </div>

        {/* Demo */}
        <div id={`${id}-demo`} className="scroll-mt-8" data-testid="costs-demo">
          <SectionHeading
            id={`${id}-demo-title`}
            tone={tone}
            eyebrow={t('demo.eyebrow')}
            title={t('demo.title')}
            lead={
              demo && typeof demo.days === 'number'
                ? t('demo.duration', { count: demo.days })
                : t('demo.durationUnknown')
            }
          />
          <dl className="grid grid-cols-1 gap-5 tablet:grid-cols-2">
            {['what', 'restricted', 'ends', 'upgrade'].map((item, index) => (
              <div key={item} className={clsx('rounded-2xl p-6', tone.card)}>
                <dt className={clsx('flex items-baseline gap-3 text-base font-semibold', tone.text)}>
                  <span aria-hidden="true" className={clsx('text-xs font-semibold tabular-nums tracking-[0.2em]', tone.accent)}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {t(`demo.${item}.title`)}
                </dt>
                <dd className={clsx('mt-2 text-sm font-light leading-relaxed', tone.muted)}>
                  {t(`demo.${item}.text`)}
                  {item === 'upgrade' && (
                    <>
                      {' '}
                      <Email tone={tone} />
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Taxes and contact */}
        <footer className={clsx('border-t pt-8 text-sm leading-relaxed', tone.divider, tone.muted)}>
          <p data-testid="costs-taxes">
            {currency ? t('taxes.withCurrency', { currency }) : t('taxes.generic')}
          </p>
          <p className="mt-2">
            {t('contact')} <Email tone={tone} />
          </p>
        </footer>
      </div>
    </section>
  )
}

export default CostsOverview
